import { Button, message, Space } from "antd";
import bem from "easy-bem";
import type { CommentData, MentionPerson } from "features/comments/data/types";
import Comment from "features/comments/elements/Comment";
import CommentForm from "features/comments/elements/CommentForm";
import { getRestrictionErrorMessage } from "features/restrictions/utils";
import { useDataQuery, useDataQueryRefetch } from "hooks/useData";
import { useFlag } from "hooks/useFlags";
import { useScrollTo } from "hooks/useScrollTo";
import { useUser } from "hooks/useUser";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { apiHost } from "../../../../consts";

import type { CommentListProps } from "./CommentList.types";
import "./CommentList.css";

const cn = bem("comment-list");

const byOldestFirst = (a: CommentData, b: CommentData) => {
  return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
};

const getParentId = (comment: CommentData): number | null => {
  if (typeof comment.parent_comment_id === "number") {
    return comment.parent_comment_id;
  }

  if (typeof comment.parentCommentId === "number") {
    return comment.parentCommentId;
  }

  if (comment.parentComment?.id) {
    return comment.parentComment.id;
  }

  return null;
};

const getMentionedIds = (comment: CommentData): number[] => {
  if (Array.isArray(comment.mentioned_person_ids)) {
    return comment.mentioned_person_ids;
  }

  if (Array.isArray(comment.mentionedPersonIds)) {
    return comment.mentionedPersonIds;
  }

  return [];
};

const getMentionedNames = (
  comment: CommentData,
  peopleById: Map<number, string>,
): string[] => {
  if (Array.isArray(comment.mentions) && comment.mentions.length > 0) {
    return comment.mentions.map((person) => {
      const fullName = [person.firstName, person.secondName || person.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

      return fullName || `Пользователь ${person.id}`;
    });
  }

  return getMentionedIds(comment).map((id) => {
    return peopleById.get(id) || `Пользователь ${id}`;
  });
};

const CommentList: React.FC<CommentListProps> = ({
  commentTargetType,
  commentTargetId,
  isModerated = false,
  // Диалог с модератором открыт сразу,
  // если он показывается, значит контент/профиль уже под модерацией
  defaultExpanded = isModerated,
  problemId,
  solutionId,
  cardId,
}) => {
  const { personId: myPersonId, isLoggedIn, isBannedFromComments } = useUser();
  const location = useLocation();
  const { scrollTo } = useScrollTo();

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Диалог с модератором ("person") — канал для обсуждения ограничений,
  // должен оставаться доступным независимо от ban_comment/block.
  const canPostComments =
    !isBannedFromComments || commentTargetType === "person";

  const moderatorialCommentsEnabled = useFlag("moderatorialCommentsEnabled");

  const canSeeModeratorialComments = isModerated && moderatorialCommentsEnabled;

  const { data: comments, refetch: refetchComments } = useDataQueryRefetch<
    CommentData[]
  >(
    `comments/?content_type=${commentTargetType}&id=${commentTargetId}&is_moderatorial=${canSeeModeratorialComments}`,
    false,
    [],
  );
  const people = useDataQuery<MentionPerson[]>("people/", false, []);
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [deletedOverrides, setDeletedOverrides] = useState<
    Map<number, boolean>
  >(new Map());

  const peopleById = useMemo(() => {
    const map = new Map<number, string>();

    (people || []).forEach((person) => {
      const fullName = [person.firstName, person.secondName || person.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

      map.set(person.id, fullName || `Пользователь ${person.id}`);
    });

    return map;
  }, [people]);

  const mentionOptions = useMemo(() => {
    return (people || [])
      .filter((person) => person.id !== myPersonId)
      .map((person) => {
        const fullName = [
          person.firstName,
          person.secondName || person.lastName,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        return {
          value: person.id,
          label: fullName || `Пользователь ${person.id}`,
        };
      });
  }, [people, myPersonId]);

  const sortedComments = useMemo(() => {
    if (!comments || !Array.isArray(comments)) {
      return [];
    }

    return [...comments].sort(byOldestFirst);
  }, [comments]);

  // Открытие модалки по ссылке на конкретный комментарий переключает
  // isModalOpen ещё до монтирования этого списка — как только комментарии
  // подгрузились, довершаем переход прокруткой к нужному.
  useEffect(() => {
    const match = location.hash.match(/~comment-(\d+)$/);

    if (!match) return;

    const targetId = Number(match[1]);

    if (!sortedComments.some((comment) => comment.id === targetId)) return;

    scrollTo(`comment-${targetId}`);
  }, [sortedComments, location.hash, scrollTo]);

  const rootComments = useMemo(() => {
    return sortedComments.filter((comment) => getParentId(comment) === null);
  }, [sortedComments]);

  const repliesByParent = useMemo(() => {
    const map = new Map<number, CommentData[]>();

    sortedComments.forEach((comment) => {
      const parentId = getParentId(comment);

      if (parentId === null) {
        return;
      }

      if (!map.has(parentId)) {
        map.set(parentId, []);
      }

      map.get(parentId)?.push(comment);
    });

    return map;
  }, [sortedComments]);

  const collectDescendantIds = useCallback(
    (commentId: number): number[] => {
      const directReplies = repliesByParent.get(commentId) || [];

      return directReplies.flatMap((reply) => [
        reply.id,
        ...collectDescendantIds(reply.id),
      ]);
    },
    [repliesByParent],
  );

  const handleDeleteToggle = useCallback(
    (commentId: number, isDeleted: boolean, recursive: boolean) => {
      const ids = recursive
        ? [commentId, ...collectDescendantIds(commentId)]
        : [commentId];

      setDeletedOverrides((prev) => {
        const next = new Map(prev);

        ids.forEach((id) => next.set(id, isDeleted));

        return next;
      });
    },
    [collectDescendantIds],
  );

  const submitComment = useCallback(
    async (
      text: string,
      mentionedPersonIds: number[] = [],
      parentCommentId?: number,
    ) => {
      const filteredMentionedPersonIds = mentionedPersonIds.filter(
        (id) => id !== myPersonId,
      );

      try {
        const response = await fetch(`${apiHost}/solutions/api/comments/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content_type: commentTargetType,
            id: commentTargetId,
            content: text,
            is_moderatorial: isModerated,
            mentioned_person_ids: filteredMentionedPersonIds,
            ...(typeof parentCommentId === "number"
              ? { parent_comment_id: parentCommentId }
              : {}),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          const restrictionMessage = getRestrictionErrorMessage(data);

          throw new Error(
            restrictionMessage || data?.error || `HTTP ${response.status}`,
          );
        }

        refetchComments();
        setReplyToId(null);

        return true;
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Не удалось отправить комментарий",
        );

        return false;
      }
    },
    [
      commentTargetType,
      commentTargetId,
      refetchComments,
      myPersonId,
      isModerated,
    ],
  );

  if (!comments || !Array.isArray(comments)) {
    return <div>Загрузка комментариев...</div>;
  }

  const renderCommentItem = (comment: CommentData, isReply = false) => {
    const replies = repliesByParent.get(comment.id) || [];
    const authorName =
      [comment.author?.firstName, comment.author?.lastName]
        .filter(Boolean)
        .join(" ") || `Пользователь ${comment.user}`;
    const mentionedPeople = getMentionedNames(comment, peopleById);
    const isDeleted = deletedOverrides.get(comment.id) ?? comment.isDeleted;

    return (
      <div
        key={comment.id}
        className={cn("reply", { nested: isReply })}
      >
        <Comment
          id={comment.id}
          text={comment.content}
          problemId={problemId}
          solutionId={solutionId}
          cardId={cardId}
          //@ts-ignore
          meta={{
            user: comment.user,
            author: comment.author?.id,
            dateCreated: comment.dateCreated,
            isDeleted,
            isModerated: comment.isModerated,
          }}
          userName={authorName}
          avatarUrl={comment.author.avatarUrl}
          mentionedPeople={mentionedPeople}
          hasChildren={replies.length > 0}
          onDeleteToggle={(newIsDeleted, recursive) =>
            handleDeleteToggle(comment.id, newIsDeleted, recursive)
          }
          allowModeratorActions={!isModerated}
        />

        {!isReply &&
          !isModerated &&
          !isDeleted &&
          isLoggedIn &&
          canPostComments && (
            <div className={cn("reply-toggle")}>
              <Button
                type="link"
                size="small"
                onClick={() =>
                  setReplyToId((prev) =>
                    prev === comment.id ? null : comment.id,
                  )
                }
              >
                {replyToId === comment.id ? "Отменить ответ" : "Ответить"}
              </Button>
            </div>
          )}

        {!isReply &&
          !isModerated &&
          !isDeleted &&
          isLoggedIn &&
          canPostComments &&
          replyToId === comment.id && (
            <div className={cn("reply-form")}>
              <CommentForm
                onSubmit={(text, mentionedIds) =>
                  submitComment(text, mentionedIds, comment.id)
                }
                mentionOptions={mentionOptions}
                canMention={!isModerated}
              />
            </div>
          )}

        {!isReply && replies.length > 0 && (
          <Space
            direction="vertical"
            size="middle"
            className={cn("replies")}
          >
            {replies.map((reply) => renderCommentItem(reply, true))}
          </Space>
        )}
      </div>
    );
  };

  const title = isModerated ? "Диалог с модератором" : "Комментарии";

  return (
    <div className={cn({ moderated: isModerated })}>
      {isModerated ? (
        <h3
          className={cn("title", { clickable: true, expanded: isExpanded })}
          onClick={() => setIsExpanded((v) => !v)}
        >
          {title} {isExpanded ? "▲" : "▼"}
        </h3>
      ) : (
        <h3 className={cn("title", { expanded: true })}>{title}</h3>
      )}

      {(!isModerated || isExpanded) && (
        <>
          <Space
            direction="vertical"
            size="large"
            className={cn("comments")}
          >
            {rootComments.length === 0 ? (
              <p>Комментариев пока нет</p>
            ) : (
              rootComments.map((comment) => renderCommentItem(comment))
            )}
          </Space>

          {isLoggedIn && canPostComments && (
            <Space
              direction="vertical"
              size="large"
              className={cn("form")}
            >
              <CommentForm
                onSubmit={(text, mentionedIds) =>
                  submitComment(text, mentionedIds)
                }
                mentionOptions={mentionOptions}
                canMention={!isModerated}
              />
            </Space>
          )}
        </>
      )}
    </div>
  );
};

export default CommentList;
